import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getSiteConfig } from "@/siteConfig";

export default function AuthShell({
    eyebrow,
    title,
    subtitle,
    children,
    sideTitle,
    sideBody,
    sideMeta = [],
}) {
    const { version } = getSiteConfig();
    return (
        <div data-testid="auth-shell" className="min-h-screen bg-bg text-ink grid lg:grid-cols-12">
            <aside className="hidden lg:flex lg:col-span-5 bg-ink text-bg p-12 xl:p-16 flex-col justify-between relative overflow-hidden">
                <div className="relative z-10 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="auth-back-home"
                        className="inline-flex items-center gap-2 text-bg/80 hover:text-bg link-underline text-sm"
                    >
                        <ArrowLeft size={14} /> Back
                    </Link>
                    <span
                        className="font-mono uppercase tracking-[0.2em] text-[11px]"
                        style={{ color: "hsl(var(--ink-mute))" }}
                    >
                        v{version}
                    </span>
                </div>

                <div className="relative z-10 max-w-md">
                    <div className="flex items-center gap-3 mb-8">
                        <span className="h-3 w-3 bg-bg" />
                        <span className="h-3 w-3 rounded-full bg-forge" />
                        <span
                            className="font-mono uppercase tracking-[0.2em] text-[11px]"
                            style={{ color: "hsl(var(--ink-mute))" }}
                        >
                            ReachForge / Customer area
                        </span>
                    </div>
                    <h2 className="font-display text-4xl xl:text-5xl font-black tracking-tight leading-[1.05] text-bg">
                        {sideTitle}
                    </h2>
                    <p className="mt-5 text-bg/70 leading-relaxed">{sideBody}</p>
                </div>

                {sideMeta.length > 0 && (
                    <div className="relative z-10 grid grid-cols-3 gap-px bg-bg/20 max-w-md">
                        {sideMeta.map((m) => (
                            <div key={`${m.k}-${m.v}`} className="bg-ink p-4">
                                <div className="font-display text-2xl font-bold text-bg">{m.k}</div>
                                <div
                                    className="font-mono uppercase tracking-[0.2em] text-[10px] mt-1"
                                    style={{ color: "hsl(var(--ink-mute))" }}
                                >
                                    {m.v}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.07] pointer-events-none"
                    style={{
                        backgroundImage:
                            "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
                        backgroundSize: "56px 56px",
                    }}
                />
            </aside>

            <main className="lg:col-span-7 flex flex-col">
                <div className="px-6 md:px-12 lg:px-16 pt-8 md:pt-10 flex items-center justify-between">
                    <Link to="/" data-testid="auth-logo" className="font-display text-xl font-black tracking-tighter">
                        ReachForge
                    </Link>
                    <Link
                        to="/"
                        data-testid="auth-back-home-mobile"
                        className="lg:hidden inline-flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink link-underline"
                    >
                        <ArrowLeft size={12} /> Home
                    </Link>
                </div>

                <div className="flex-1 flex items-center px-6 md:px-12 lg:px-16 py-12 md:py-16">
                    <div className="w-full max-w-[480px] mx-auto lg:mx-0">
                        <span data-testid="auth-eyebrow" className="overline">
                            {eyebrow}
                        </span>
                        <h1
                            data-testid="auth-title"
                            className="font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mt-3 text-ink"
                        >
                            {title}
                        </h1>
                        {subtitle && <p className="mt-4 text-ink-soft max-w-md">{subtitle}</p>}
                        <div className="mt-10">{children}</div>
                    </div>
                </div>

                <footer className="px-6 md:px-12 lg:px-16 py-6 border-t border-border flex flex-col sm:flex-row gap-2 sm:gap-4 justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-ink-mute">
                    <span>
                        © {new Date().getFullYear()} ReachForge ·{" "}
                        <a href="/#website-request" className="hover:text-ink link-underline">
                            Request a website
                        </a>
                    </span>
                    <div className="flex gap-4">
                        <a href="/privacy" className="hover:text-ink link-underline">Privacy</a>
                        <a href="/terms" className="hover:text-ink link-underline">Terms</a>
                        <a href="mailto:support@reachforge.io" className="hover:text-ink link-underline">Support</a>
                    </div>
                </footer>
            </main>
        </div>
    );
}
